/**
 * Página de Lista de Integraciones
 * Muestra tarjetas de integraciones con filtro mágico, clonar y refresh sutil
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { integracionesAPI } from '../utils/api';
import { toast } from 'sonner';
import {
  Plug,
  Plus,
  Search,
  Settings,
  Trash2,
  Copy,
  Loader2,
  Info,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Integraciones = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Estados principales
  const [integraciones, setIntegraciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog de confirmación de eliminación
  const [deleteDialog, setDeleteDialog] = useState({ open: false, integracion: null });
  
  // Cargar datos iniciales
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await integracionesAPI.getAll();
      setIntegraciones(response.data || []);
    } catch (error) {
      toast.error('Error al cargar integraciones');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleRefresh = () => {
    loadData(true);
  };
  
  // Filtrar integraciones
  const integracionesFiltradas = useMemo(() => {
    if (!searchTerm) return integraciones;
    
    const term = searchTerm.toLowerCase();
    return integraciones.filter(i => 
      i.nombre.toLowerCase().includes(term) ||
      (i.descripcion && i.descripcion.toLowerCase().includes(term)) ||
      JSON.stringify(i.parametros_operacion || {}).toLowerCase().includes(term)
    );
  }, [integraciones, searchTerm]);
  
  // Navegar a crear nueva
  const handleCreate = () => {
    navigate('/integraciones/nueva');
  };
  
  // Navegar a configurar
  const handleConfig = (codigo) => {
    navigate(`/integraciones/${codigo}`);
  };
  
  // Clonar integración
  const handleClone = (integracion) => {
    // Guardar datos para clonar en sessionStorage
    const cloneData = {
      ...integracion,
      codigo: `${integracion.codigo}_copia`,
      nombre: `${integracion.nombre} (Copia)`,
      isClone: true
    };
    sessionStorage.setItem('integracion_clone', JSON.stringify(cloneData));
    navigate('/integraciones/nueva');
    toast.info('Modifica el código y nombre antes de guardar');
  };
  
  // Confirmar eliminación
  const handleDeleteConfirm = (integracion) => {
    setDeleteDialog({ open: true, integracion });
  };
  
  // Eliminar integración
  const handleDelete = async () => {
    const { integracion } = deleteDialog;
    if (!integracion) return;
    
    try {
      await integracionesAPI.delete(integracion.codigo);
      toast.success('Integración eliminada correctamente');
      setDeleteDialog({ open: false, integracion: null });
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar integración');
      console.error(error);
    }
  };
  
  // Logo por defecto
  const getLogoUrl = (integracion) => {
    if (integracion.logo_url) return integracion.logo_url;
    // Logo placeholder basado en el nombre
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(integracion.nombre)}&background=9d8272&color=fff&size=128`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#9d8272]" />
      </div>
    );
  }
  
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#333333] flex items-center gap-2">
            <Plug className="w-6 h-6 text-[#9d8272]" />
            Integraciones
          </h1>
          <p className="text-sm text-[#888888] mt-1">
            Configura las integraciones con plataformas externas
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[#9d8272] hover:bg-[#8c7161] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Integración
        </Button>
      </div>
      
      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
              <Input
                placeholder="Buscar por nombre, descripción o parámetros..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#c5b8a2]"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-[#c5b8a2]"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Lista de integraciones */}
      <div className="relative">
        {/* Overlay sutil de refresh */}
        {refreshing && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
              <Loader2 className="w-5 h-5 animate-spin text-[#9d8272]" />
              <span className="text-sm text-[#666666]">Actualizando...</span>
            </div>
          </div>
        )}
        
        {integracionesFiltradas.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Info className="w-12 h-12 text-[#c5b8a2] mx-auto mb-4" />
              <p className="text-[#888888]">
                {searchTerm
                  ? 'No se encontraron integraciones con los filtros aplicados'
                  : 'No hay integraciones configuradas'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {integracionesFiltradas.map(integracion => (
              <Card 
                key={integracion.codigo}
                className="overflow-hidden hover:shadow-lg transition-shadow duration-200"
              >
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-start justify-between gap-3">
                    {/* Logo */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#f8f6f4] flex-shrink-0 flex items-center justify-center">
                      <img
                        src={getLogoUrl(integracion)}
                        alt={integracion.nombre}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(integracion.nombre)}&background=9d8272&color=fff&size=128`;
                        }}
                      />
                    </div>
                    
                    {/* Estado */}
                    <Badge 
                      variant="outline"
                      className={`text-xs ${
                        integracion.activo 
                          ? 'border-green-300 text-green-700 bg-green-50' 
                          : 'border-gray-300 text-gray-500 bg-gray-50'
                      }`}
                    >
                      {integracion.activo ? (
                        <><CheckCircle2 className="w-3 h-3 mr-1" /> Activo</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> Inactivo</>
                      )}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="p-4 pt-2">
                  {/* Nombre y descripción */}
                  <CardTitle className="text-base font-semibold text-[#333333] mb-1">
                    {integracion.nombre}
                  </CardTitle>
                  <p className="text-xs text-[#888888] line-clamp-2 min-h-[32px]">
                    {integracion.descripcion || 'Sin descripción'}
                  </p>
                  
                  {/* Info adicional */}
                  <div className="mt-3 flex items-center gap-2 text-xs text-[#888888]">
                    <code className="bg-[#f0ece8] px-2 py-0.5 rounded font-mono text-[#9d8272] truncate max-w-[150px]">
                      {integracion.codigo}
                    </code>
                    {integracion.url_api_salida && (
                      <ExternalLink className="w-3 h-3" />
                    )}
                  </div>
                  
                  {/* Acciones */}
                  <div className="mt-4 flex items-center justify-end gap-1 border-t border-[#e5e0da] pt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleClone(integracion)}
                      className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                      title="Clonar integración"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleConfig(integracion.codigo)}
                      className="text-[#9d8272] hover:text-[#8c7161] hover:bg-[#f0ece8]"
                      title="Configurar integración"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteConfirm(integracion)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Eliminar integración"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, integracion: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Eliminar Integración
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar la integración <strong>{deleteDialog.integracion?.nombre}</strong>?
              <br />
              <span className="text-xs text-[#888888]">
                Código: {deleteDialog.integracion?.codigo}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#c5b8a2]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Integraciones;
